import React from 'react';
import { Chip, Avatar } from '@mui/material';
import { styled } from '@mui/material/styles';
import PropTypes from 'prop-types';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import InfoIcon from '@mui/icons-material/Info';
import PendingIcon from '@mui/icons-material/Pending';
import CancelIcon from '@mui/icons-material/Cancel';

const StyledChip = styled(Chip)(({ theme, status }) => {
  const statusStyles = {
    active: {
      backgroundColor: theme.palette.success.light,
      color: theme.palette.success.contrastText,
      '& .MuiChip-icon': {
        color: theme.palette.success.main,
      },
    },
    inactive: {
      backgroundColor: theme.palette.grey[300],
      color: theme.palette.text.secondary,
      '& .MuiChip-icon': {
        color: theme.palette.grey[600],
      },
    },
    pending: {
      backgroundColor: theme.palette.warning.light,
      color: theme.palette.warning.contrastText,
      '& .MuiChip-icon': {
        color: theme.palette.warning.main,
      },
    },
    error: {
      backgroundColor: theme.palette.error.light,
      color: theme.palette.error.contrastText,
      '& .MuiChip-icon': {
        color: theme.palette.error.main,
      },
    },
    success: {
      backgroundColor: theme.palette.success.light,
      color: theme.palette.success.contrastText,
      '& .MuiChip-icon': {
        color: theme.palette.success.main,
      },
    },
    warning: {
      backgroundColor: theme.palette.warning.light,
      color: theme.palette.warning.contrastText,
      '& .MuiChip-icon': {
        color: theme.palette.warning.main,
      },
    },
    info: {
      backgroundColor: theme.palette.info.light,
      color: theme.palette.info.contrastText,
      '& .MuiChip-icon': {
        color: theme.palette.info.main,
      },
    },
    online: {
      backgroundColor: '#e8f5e8',
      color: '#2e7d32',
      '& .MuiChip-icon': {
        color: '#4caf50',
      },
    },
    offline: {
      backgroundColor: '#ffebee',
      color: '#c62828',
      '& .MuiChip-icon': {
        color: '#f44336',
      },
    },
    draft: {
      backgroundColor: theme.palette.grey[200],
      color: theme.palette.text.secondary,
      '& .MuiChip-icon': {
        color: theme.palette.grey[500],
      },
    },
    published: {
      backgroundColor: '#e3f2fd',
      color: '#1565c0',
      '& .MuiChip-icon': {
        color: '#2196f3',
      },
    },
  };

  return {
    fontWeight: 500,
    fontSize: '0.75rem',
    height: '24px',
    borderRadius: '12px',
    ...statusStyles[status],
  };
});

// Status icon mapping
const statusIcons = {
  active: CheckCircleIcon,
  inactive: CancelIcon,
  pending: PendingIcon,
  error: ErrorIcon,
  success: CheckCircleIcon,
  warning: WarningIcon,
  info: InfoIcon,
  online: CheckCircleIcon,
  offline: CancelIcon,
  draft: PendingIcon,
  published: CheckCircleIcon,
};

// Status label mapping
const statusLabels = {
  active: 'Active',
  inactive: 'Inactive',
  pending: 'Pending',
  error: 'Error',
  success: 'Success',
  warning: 'Warning',
  info: 'Info',
  online: 'Online',
  offline: 'Offline',
  draft: 'Draft',
  published: 'Published',
};

/**
 * StatusChip component for displaying status indicators with consistent styling
 *
 * @param {Object} props - Component props
 * @param {string} props.status - Status type (active, inactive, pending, etc.)
 * @param {string} props.label - Custom label (overrides default status label)
 * @param {React.ReactNode} props.icon - Custom icon (overrides default status icon)
 * @param {boolean} props.showIcon - Show status icon
 * @param {string} props.size - Chip size
 * @param {string} props.variant - Chip variant
 * @param {function} props.onClick - Click handler
 * @param {function} props.onDelete - Delete handler
 * @param {boolean} props.clickable - Make chip clickable
 */
const StatusChip = ({
  status,
  label,
  icon,
  showIcon = true,
  size = 'small',
  variant = 'filled',
  onClick,
  onDelete,
  clickable = false,
  ...otherProps
}) => {
  // Get default label and icon for status
  const defaultLabel = statusLabels[status] || status;
  const IconComponent = statusIcons[status];

  // Determine final label and icon
  const finalLabel = label || defaultLabel;
  const finalIcon = icon || (showIcon && IconComponent ? <IconComponent /> : undefined);

  return (
    <StyledChip
      status={status}
      label={finalLabel}
      icon={finalIcon}
      size={size}
      variant={variant}
      onClick={onClick}
      onDelete={onDelete}
      clickable={clickable || !!onClick}
      {...otherProps}
    />
  );
};

StatusChip.propTypes = {
  status: PropTypes.oneOf([
    'active',
    'inactive',
    'pending',
    'error',
    'success',
    'warning',
    'info',
    'online',
    'offline',
    'draft',
    'published',
  ]).isRequired,
  label: PropTypes.string,
  icon: PropTypes.node,
  showIcon: PropTypes.bool,
  size: PropTypes.oneOf(['small', 'medium']),
  variant: PropTypes.oneOf(['filled', 'outlined']),
  onClick: PropTypes.func,
  onDelete: PropTypes.func,
  clickable: PropTypes.bool,
};

export default StatusChip;
